import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, Shield } from "lucide-react";

import { Button } from "@/ui";
import {
  useProxyConfig,
  useTestProxyConfig,
  useUpdateProxyConfig,
  type ProxyStatus,
} from "@/features/queries";

/**
 * Proxy settings for the outbound Telegram bot session.
 *
 * Prod runs on a Russian-hosting box that can't always reach
 * api.telegram.org directly; this page lets an admin paste a
 * SOCKS5/HTTP proxy URL, probe it, and flip it live for the whole
 * process without a redeploy. The header dot in AdminShell picks up
 * the stored `status` through the same query.
 */
export function AdminProxyPage() {
  const { data, isLoading } = useProxyConfig();
  const save = useUpdateProxyConfig();
  const test = useTestProxyConfig();

  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(false);

  // Seed the form with saved values on first load, not on every refetch
  // — a background refetch mustn't overwrite what the admin is typing.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (data && !seeded) {
      setUrl(data.url ?? "");
      setEnabled(data.enabled);
      setSeeded(true);
    }
  }, [data, seeded]);

  const dirty =
    data != null && (url !== (data.url ?? "") || enabled !== data.enabled);

  return (
    <div style={{ maxWidth: 640 }}>
      <h1 className="h1" style={{ marginTop: 0 }}>Прокси для Telegram</h1>
      <p className="body" style={{ color: "var(--fg2)" }}>
        Сессия бота идёт через этот прокси при общении с <code>api.telegram.org</code>.
        Нужен SOCKS5 или HTTP/HTTPS. Остальной трафик сервера не затрагивается.
      </p>

      <StatusRow
        status={data?.status ?? "disabled"}
        lastCheckedAt={data?.last_checked_at ?? null}
        lastError={data?.last_error ?? null}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ url: url.trim() || null, enabled });
        }}
        style={{
          marginTop: 20,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          background: "var(--bg-section)",
          borderRadius: "var(--r-lg)",
          padding: 20,
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="label" style={{ color: "var(--fg2)" }}>URL прокси</span>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="socks5://user:pass@host:1080"
            spellCheck={false}
            autoComplete="off"
            style={{
              border: "1px solid var(--separator)",
              borderRadius: "var(--r-md)",
              padding: "10px 14px",
              fontFamily: "var(--font-mono, monospace)",
              fontSize: 14,
              background: "var(--bg)",
              color: "var(--fg1)",
              outline: "none",
            }}
          />
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span className="body">Использовать прокси</span>
        </label>

        {save.error && (
          <div style={{ color: "var(--fg-danger)", fontSize: 13 }}>
            {(save.error as Error).message}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button
            type="submit"
            disabled={save.isPending || !dirty || isLoading}
          >
            {save.isPending ? "Сохраняем…" : "Сохранить и применить"}
          </Button>
          <Button
            variant="secondary"
            type="button"
            disabled={test.isPending || !url.trim()}
            onClick={() => test.mutate({ url: url.trim() })}
          >
            {test.isPending ? "Проверяем…" : "Проверить сейчас"}
          </Button>
        </div>
      </form>

      <div
        className="caption"
        style={{ color: "var(--fg3)", marginTop: 12, lineHeight: 1.5 }}
      >
        Примеры: <code>socks5://login:pass@1.2.3.4:1080</code>,
        {" "}<code>http://1.2.3.4:3128</code>. MTProto-прокси для Bot API не
        подходит — только HTTP(S)/SOCKS.
      </div>
    </div>
  );
}

function StatusRow({
  status,
  lastCheckedAt,
  lastError,
}: {
  status: ProxyStatus;
  lastCheckedAt: string | null;
  lastError: string | null;
}) {
  const { color, Icon, label } = statusMeta(status);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 14px",
        background: "var(--bg-section)",
        border: `1px solid ${color}33`,
        borderRadius: "var(--r-md)",
        marginTop: 16,
      }}
    >
      <Icon size={20} color={color} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="body" style={{ fontWeight: 600, color }}>
          {label}
        </div>
        <div className="caption" style={{ color: "var(--fg2)" }}>
          {lastCheckedAt
            ? `Последняя проверка: ${formatTime(lastCheckedAt)}`
            : "Ещё не проверялось"}
          {lastError ? ` · ${lastError}` : ""}
        </div>
      </div>
    </div>
  );
}

function statusMeta(s: ProxyStatus): {
  color: string;
  Icon: typeof CheckCircle2;
  label: string;
} {
  switch (s) {
    case "ok":
      return { color: "#1E7A5A", Icon: CheckCircle2, label: "Прокси работает" };
    case "fail":
      return { color: "#DF3F40", Icon: AlertCircle, label: "Прокси не отвечает" };
    case "checking":
      return { color: "#9AA0A6", Icon: Loader2, label: "Проверяем…" };
    case "disabled":
    default:
      return { color: "#9AA0A6", Icon: Shield, label: "Прокси отключён" };
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Re-export so AdminShell can reuse the same status→colour mapping for
// the sidebar dot.
export { statusMeta as proxyStatusMeta };
